import React, { useState } from "react";
import { Dialog } from "./dialog";
import { StandardFrame } from "../frames/standard-frame";
import { SimpleButton } from "../buttons/simple-button";
import { AnimatedVFX } from "../../vfx/animated-vfx";
import { claimDailyReward } from "@/services/player/claim-daily-reward";
import { useAuth } from "@/providers/auth-provider";
import { useUser } from "@/providers/user-provider";
import { Button } from "../buttons/button";

interface DailyRewardDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

export const DailyRewardDialog = ({
  isOpen,
  onClose,
}: DailyRewardDialogProps) => {
  const { getAccessToken } = useAuth();
  const { setPokeShards, reloadProfile } = useUser();
  const [isClaiming, setIsClaiming] = useState(false);
  const [claimedAmount, setClaimedAmount] = useState<number | null>(null);
  const [showVFX, setShowVFX] = useState(false);

  const handleClaim = async () => {
    const token = getAccessToken();
    if (!token || isClaiming) return;

    setIsClaiming(true);
    try {
      const response = await claimDailyReward(token);
      if (response.ok && response.data?.reward) {
        const amount = response.data.reward.shards;
        setClaimedAmount(amount);
        setShowVFX(true);

        // Update local state
        setPokeShards((prev) => (prev !== null ? prev + amount : amount));

        // Refresh profile to sync everything
        await reloadProfile();
      } else {
        console.error("Failed to claim reward:", response.message);
        onClose();
      }
    } catch (error) {
      console.error("Error claiming daily reward:", error);
      onClose();
    } finally {
      setIsClaiming(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Dialog isOpen={isOpen} onClose={claimedAmount ? onClose : () => {}}>
      <div className="px-2 mx-auto min-w-[80vw] sm:min-w-[40vw] relative">
        {showVFX && (
          <div className="absolute inset-0 pointer-events-none z-50 flex items-center justify-center">
            <AnimatedVFX
              id="daily-reward-sparkles"
              spriteUrl="/assets/spells/vfx/self_sparkles_000__48.png"
              position={{ x: 150, y: 100 }} // Centered relative to container
              spriteScale={2}
              onComplete={() => setShowVFX(false)}
            />
          </div>
        )}

        <StandardFrame variant="green" className="max-w-md w-full">
          <div className="text-center mt-3 p-4">
            <h2 className="text-3xl font-black text-pk-gray-4 mb-2 tracking-widest drop-shadow-md uppercase">
              Welcome Back!
            </h2>

            {!claimedAmount ? (
              <>
                <p className="text-pk-beige-5 mb-8 text-sm font-medium">
                  Your daily supply of shards has arrived!
                </p>

                <div className="relative flex flex-col items-center justify-center mb-10 py-6 px-10 bg-black/20 rounded-xl border border-white/10 shadow-inner">
                  {/* Glow effect behind the shard */}
                  <div className="absolute w-32 h-32 bg-pk-blue-5/20 rounded-full blur-2xl animate-pulse" />

                  <div className="relative w-24 h-24 mb-4 transition-transform hover:scale-110 duration-300">
                    <img
                      src="/assets/app-ui/poke-shard.png"
                      alt="PokeShard"
                      className="w-full h-full object-contain drop-shadow-[0_0_15px_rgba(59,130,246,0.5)]"
                    />
                  </div>
                  <p className="text-pk-gray-4 font-black text-xl tracking-widest uppercase drop-shadow-sm">
                    PokeShards
                  </p>
                </div>

                <div className="flex justify-center w-full">
                  <Button
                    onClick={handleClaim}
                    loading={isClaiming}
                    className="w-full text-pk-gray-4 max-w-[200px] py-3 text-lg font-black tracking-wide shadow-lg hover:shadow-xl transition-all"
                    variant="green"
                  >
                    {isClaiming ? (
                      <span className="flex items-center gap-2">
                        <span className="animate-spin">✨</span> CLAIMING...
                      </span>
                    ) : (
                      "CLAIM REWARD"
                    )}
                  </Button>
                </div>
              </>
            ) : (
              <div className="animate-in fade-in zoom-in duration-500 flex flex-col items-center">
                <p className="text-pk-green-4 font-black text-xl mb-6 tracking-wide uppercase drop-shadow-sm">
                  Reward Claimed!
                </p>

                <div className="bg-gradient-to-b from-pk-blue-5/20 to-pk-blue-5/5 border border-pk-blue-5/30 rounded-xl p-6 mb-8 w-full shadow-lg relative overflow-hidden">
                  <div className="absolute inset-0 bg-pk-blue-5/10 blur-xl" />
                  <div className="relative flex flex-col items-center justify-center gap-3">
                    <img
                      src="/assets/app-ui/poke-shard.png"
                      alt="PokeShard"
                      className="w-16 h-16 object-contain drop-shadow-lg mb-2"
                    />
                    <div className="flex items-baseline gap-2">
                      <span className="text-5xl font-black text-pk-white drop-shadow-md">
                        {claimedAmount}
                      </span>
                      <span className="text-pk-blue-3 font-bold text-lg uppercase tracking-wider">
                        Shards
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex justify-center w-full">
                  <SimpleButton
                    onClick={onClose}
                    className="w-full max-w-[200px] py-3 text-lg font-black tracking-wide shadow-lg"
                    variant="green"
                  >
                    AWESOME!
                  </SimpleButton>
                </div>
              </div>
            )}
          </div>
        </StandardFrame>
      </div>
    </Dialog>
  );
};
